import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  useToast,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import Select from "@/components/Common/AppSelect"
import type {
  WarehouseLocationTemplateDTO,
  WarehousesDTO,
} from "../../../client"
import { WareHouseService, type WarehouseCreateDTO } from "../../../client"

type Props = {
  onSuccess?: (warehouse: WarehousesDTO) => void
  onCancel?: () => void
}

export function AddWarehouse({ onSuccess, onCancel }: Props) {
  const toast = useToast()
  const qc = useQueryClient()

  const [code, setCode] = useState("")
  const [name, setName] = useState("")
  const [locationTemplateId, setLocationTemplateId] = useState("")

  const { data: templates = [] } = useQuery<WarehouseLocationTemplateDTO[]>({
    queryKey: ["wms", "warehouse", "location-templates"],
    queryFn: () => WareHouseService.listLocationTemplates(),
  })

  const isCodeErr = code.trim() === ""
  const isNameErr = name.trim() === ""

  const { mutate, isPending } = useMutation({
    mutationKey: ["wms", "warehouses", "create"],
    mutationFn: (payload: WarehouseCreateDTO) =>
      WareHouseService.createWarehouse(payload),
    onSuccess: async (created) => {
      toast({ title: "Magazyn został stworzony", status: "success" })
      await qc.invalidateQueries({ queryKey: ["wms", "warehouses", "list"] })
      onSuccess?.(created)
      setCode("")
      setName("")
      setLocationTemplateId("")
    },
    onError: (err: any) => {
      toast({
        title: "Błąd tworzenia",
        description: err?.message ?? "Spróbuj ponownie",
        status: "error",
      })
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (isCodeErr || isNameErr) return
    const payload: WarehouseCreateDTO = {
      code: code.trim(),
      name: name.trim(),
      ...(locationTemplateId
        ? { location_template_id: locationTemplateId }
        : {}),
    }
    mutate(payload)
  }

  return (
    <VStack as="form" onSubmit={handleSubmit} align="stretch" spacing={4}>
      <FormControl isInvalid={isCodeErr} isRequired>
        <FormLabel>Kod</FormLabel>
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          onBlur={(e) => setCode(e.target.value.toUpperCase().trim())}
          placeholder="np. SUR-1"
        />
        <FormErrorMessage>Pole Kod jest obowiązkowe</FormErrorMessage>
      </FormControl>

      <FormControl isInvalid={isNameErr} isRequired>
        <FormLabel>Nazwa</FormLabel>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="np. Magazyn Surowców"
        />
        <FormErrorMessage>Pole Nazwa jest obowiązkowe</FormErrorMessage>
      </FormControl>

      <FormControl>
        <FormLabel>Szablon lokalizacji</FormLabel>
        <Select
          value={locationTemplateId}
          onChange={(e) => setLocationTemplateId(e.target.value)}
        >
          <option value="">Domyślny (dotychczasowy)</option>
          {templates.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </Select>
      </FormControl>

      <HStack justify="flex-end">
        <Button variant="ghost" onClick={onCancel} type="button">
          Anuluj
        </Button>
        <Button
          colorScheme="green"
          type="submit"
          isLoading={isPending}
          isDisabled={isCodeErr || isNameErr}
        >
          Utwórz
        </Button>
      </HStack>
    </VStack>
  )
}
